import { UserReturn } from "../../../domain/Account/schema"
import { UserOnboardingStepsEnum } from "../../../domain/Account/userSlice"
import { AccountUsernamesByUserIdsResponse } from "../../../domain/PracticeProviders/usePracticeIndividualsForPractice"
import { UserAccount } from "../../../domain/PracticeProviders/useUsersDataForUserIds"
import { SiteOutageMessageState } from "../../../domain/Account/userSlice"
import { buildUrl, getData, postData, putData } from "../apiService"

interface SiteOutageMessageDto {
  message: string
  isUserAccessDisabled: boolean
  isEnabled: boolean
}
export interface CreateSiteOutageMessageRequest extends SiteOutageMessageDto {}
export interface UpdateSiteOutageMessageRequest extends SiteOutageMessageDto {}

const apiRoutes = {
  get: {
    USER_BY_ID_ROUTE: "/account/users/:userId",
    USER_BY_EMAIL_ROUTE: "/account/users/by/:userEmail",
    USERNAME_BY_USER_ID_ROUTE: "/account/usernames/:userId",
    SITE_OUTAGE_MESSAGE_ROUTE: "/account/support/siteOutageMessage",
  },
  post: {
    USENAMES_BY_USER_IDS_ROUTE: "/account/usernames",
    SITE_OUTAGE_MESSAGE_ROUTE: "/account/support/siteOutageMessage",
  },
  put: {
    COMPLETED_ONBOARDING_STEPS_ROUTE: "/account/completedOnboardingSteps",
    SITE_OUTAGE_MESSAGE_ROUTE: "/account/support/siteOutageMessage",
  },
}

const accountUserByUserEmail = async ({ userEmail }: { userEmail: string }) => {
  return getData<UserReturn>(
    buildUrl(apiRoutes.get.USER_BY_EMAIL_ROUTE, { userEmail }),
  )
}

const accountUserByUserId = async ({
  userId,
}: {
  userId: string
}): Promise<UserReturn> => {
  return getData(buildUrl(apiRoutes.get.USER_BY_ID_ROUTE, { userId }))
}

const accountUsernameByUserId = async ({ userId }: { userId: string }) => {
  return getData<UserAccount>(
    buildUrl(apiRoutes.get.USERNAME_BY_USER_ID_ROUTE, { userId }),
  )
}

const accountUsernamesByUserIds = async ({
  userIds,
}: {
  userIds: string[]
}): Promise<UserAccount[]> => {
  const response = await getData<AccountUsernamesByUserIdsResponse>(
    apiRoutes.post.USENAMES_BY_USER_IDS_ROUTE,
    true,
    userIds,
  )
  return response.$values
}

const putCompletedOnboardingSteps = async ({
  userId,
  completedOnboardingSteps,
}: {
  userId: string
  completedOnboardingSteps: UserOnboardingStepsEnum[]
}) => {
  return putData(apiRoutes.put.COMPLETED_ONBOARDING_STEPS_ROUTE, {
    userId,
    completedUserOnboardingSteps: completedOnboardingSteps,
  })
}

const getSiteOutageMessage =
  async (): Promise<SiteOutageMessageState | null> => {
    try {
      return await getData<SiteOutageMessageState>(
        apiRoutes.get.SITE_OUTAGE_MESSAGE_ROUTE,
      )
    } catch {
      return null
    }
  }

const postSiteOutageMessage = async (
  request: CreateSiteOutageMessageRequest,
): Promise<SiteOutageMessageState> => {
  return postData<SiteOutageMessageState>(
    apiRoutes.post.SITE_OUTAGE_MESSAGE_ROUTE,
    request,
  )
}

const putSiteOutageMessage = async (
  request: UpdateSiteOutageMessageRequest,
): Promise<SiteOutageMessageState> => {
  return putData<SiteOutageMessageState>(
    apiRoutes.put.SITE_OUTAGE_MESSAGE_ROUTE,
    request,
  )
}

export const accountApi = {
  get: {
    USER_BY_EMAIL: accountUserByUserEmail,
    USER_BY_ID: accountUserByUserId,
    USERNAME_BY_USER_ID: accountUsernameByUserId,
    SITE_OUTAGE_MESSAGE: getSiteOutageMessage,
  },
  post: {
    USERNAMES_BY_USER_IDS: accountUsernamesByUserIds,
    SITE_OUTAGE_MESSAGE: postSiteOutageMessage,
  },
  put: {
    COMPLETED_ONBOARDING_STEPS: putCompletedOnboardingSteps,
    SITE_OUTAGE_MESSAGE: putSiteOutageMessage,
  },
}
